import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll } from 'framer-motion';
import { ArrowRightIcon, GiftIcon, RepeatIcon } from 'lucide-react';
import { useLayerY } from '../../hooks/useParallax';
import { screens, useMediaQuery } from '../../hooks/useMediaQuery';
import { RoutineCard } from '../shared/RoutineCard';
import { HeadingText } from '../shared/HeadingText';
import { GiftTierList } from '../routines/GiftTierList';
import { useCatalog } from '../../contexts/CatalogContext';
import { useFeatures } from '../../hooks/useFeatures';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import { featuredRoutines } from './homeRules';

/**
 * "Het complete plan, kant-en-klaar": de uitgelichte protocollen naast de cadeaustaffel, en het
 * abonnement als één regel eronder. Zonder protocollen geen blok.
 */
export function HomeProtocols() {
  const pages = usePages();
  const {
    routines
  } = useCatalog();
  const {
    subscriptions
  } = useFeatures();
  const featured = featuredRoutines(routines);
  const copy = pages.home.protocols;
  // De tweede kolom kaarten scrolt een fractie sneller: net genoeg om ruimte te voelen.
  const ref = useRef<HTMLUListElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const layerY = useLayerY(scrollYProgress, [24, -24]);
  // Alleen zinvol naast elkaar: onder elkaar (telefoon) is een verschoven kaart gewoon scheef.
  const twoColumns = useMediaQuery(screens.md);
  const offsetY = twoColumns ? layerY : undefined;
  if (featured.length === 0) return null;
  return <section className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="home-protocols" data-id="element-7441" data-mp="322u5sxb6aha">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7442" data-mp="qrx7eblji122">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-7443" data-mp="qqcd8n350k4q">
          <div className="lg:col-span-4" data-id="element-7444" data-mp="vivnba2m8x4s">
            <h2 id="home-protocols" className={`${sectionHeading} text-ink`} data-id="element-7445" data-mp="vjx0pl73tvpo">
              <HeadingText heading={copy.heading} data-id="element-7446" data-mp="15db2cbeiv2ga" />
            </h2>
            {copy.body && <p className="mt-6 text-[17px] leading-relaxed text-muted" data-id="element-7447" data-mp="11cr04tf3d8na">{fill(copy.body)}</p>}

            <GiftTierList className="mt-8" data-id="element-7448" data-mp="wd4oupitq2h4" />
            <p className="mt-4 flex items-start gap-2 text-[14px] leading-relaxed text-muted" data-id="element-7449" data-mp="wg0oqje8f187">
              <GiftIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7450" data-mp="rel4uunsj0re" />
              {fill(copy.note)}
            </p>

            {copy.cta && <Link to="/routines" className={`mt-6 ${quietLink} text-ink`} data-id="element-7451" data-mp="hngpvuy47fwk">
                {fill(copy.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7452" data-mp="dnobh1m1ifja" />
              </Link>}
          </div>

          {/*
            Telefoon: een protocolkaart is lang (producten, wat erbij zit, knop). Onder elkaar zijn
            het twee schermen scrollen; daarom naast elkaar om doorheen te vegen, met de volgende
            kaart half in beeld. Vanaf tablet gewoon naast elkaar.
           */}
          <ul ref={ref} aria-label="Uitgelichte protocollen" className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-6 px-6 pb-2 [scrollbar-width:none] md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0 lg:col-span-8 [&::-webkit-scrollbar]:hidden" data-id="element-7453" data-mp="3pqfxv1o6op7a">
            {featured.map((r, i) => <motion.li key={r.id} style={i % 2 === 1 && offsetY ? {
            y: offsetY
          } : undefined} className={`h-auto shrink-0 snap-start bg-sand p-6 sm:p-7 md:w-auto lg:p-9 ${featured.length > 1 ? 'w-[86%]' : 'w-full'}`} data-id="element-7454" data-mp="31zahmlebzuwg" data-mpi={i}>
                <RoutineCard routine={r} data-id="element-7455" data-mp="3grch44cholvy" />
              </motion.li>)}
          </ul>
        </div>

        {/* Het abonnement als één regel: de uitleg zelf staat bij Protocollen en in de webshop. */}
        {subscriptions && <Link to="/routines" className="group mt-14 flex flex-col gap-2 border-t border-line pt-7 sm:flex-row sm:items-center sm:justify-between sm:gap-8" data-id="element-7456" data-mp="tsyxc4xzlqpd">
            <span className="flex items-start gap-3 text-[16px] leading-relaxed text-ink" data-id="element-7457" data-mp="osc9on8voxcg">
              <RepeatIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7458" data-mp="13kmi5n01g9hv" />
              <span data-id="element-7459" data-mp="yqia10f1dvf7">
                <span className="font-display text-xl" data-id="element-7460" data-mp="1cjzt6k3t964p">{pages.subscription.name}</span>
                <span className="text-muted" data-id="element-7461" data-mp="1clui09i7die2"> · {pages.subscription.claim}</span>
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-2 pl-7 text-[15px] text-ink group-hover:underline group-hover:underline-offset-[6px] sm:pl-0" data-id="element-7462" data-mp="1mhxfh2240suo">
              Zo werkt het abonnement
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7463" data-mp="3o9ai1kgluonk" />
            </span>
          </Link>}
      </div>
    </section>;
}